import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, type DatePickerProps } from 'antd';
import styled from 'styled-components';

import { LARGE_FIELD } from './constants';
import { Container, ErrorText, Label, RequiredStar } from './form-shared';

const largeStyles = `
  width: 100%;
  height: ${LARGE_FIELD.height}px;

  .ant-picker-input > input,
  .ant-picker-input > input::placeholder {
    font-size: ${LARGE_FIELD.fontSize}px !important;
  }
`;

interface StyledDatePickerProps {
  $fullWidth?: boolean;
  $large?: boolean;
}

const StyledDatePicker = styled(DatePicker)<StyledDatePickerProps>`
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  ${({ $large }) => $large && largeStyles}
`;

interface FormDatePickerFieldProps extends Omit<DatePickerProps, 'size'> {
  label?: string;
  style?: React.CSSProperties;
  name: string;
  size?: 'large' | 'middle' | 'small';
  fullWidth?: boolean;
  required?: boolean;
}

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

function FormDatePickerField({
  label,
  style,
  name,
  size = 'large',
  fullWidth,
  required,
  format = 'MM/DD/YYYY',
  ...rest
}: FormDatePickerFieldProps) {
  const ctx = useFormContext();
  const control = ctx?.control ?? undefined;
  const isLarge = size === 'large';

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <Container style={style} $fullWidth={fullWidth}>
          {label && (
            <Label>
              {label}
              {required && <RequiredStar>*</RequiredStar>}
            </Label>
          )}
          {React.createElement(StyledDatePicker, {
            value: field.value,
            onChange: field.onChange,
            format,
            $large: isLarge,
            $fullWidth: fullWidth,
            inputReadOnly: isTouchDevice(),
            ...rest
          })}
          {error && <ErrorText>{error?.message}</ErrorText>}
        </Container>
      )}
    />
  );
}

export default FormDatePickerField;
